import { Router, type IRouter } from "express";
import { db, dealRoomsTable, dealRoomItemsTable, bidsTable, dealsTable, usersTable } from "@workspace/db";
import { eq, and, count, sql, inArray } from "drizzle-orm";
import { requireAuth, requireRole } from "../middlewares/auth";

const router: IRouter = Router();

function formatDealForRoom(deal: typeof dealsTable.$inferSelect) {
  return {
    id: deal.id,
    title: deal.title,
    area: deal.area,
    building: deal.building,
    unitType: deal.unitType,
    bedrooms: deal.bedrooms,
    bathrooms: deal.bathrooms,
    sizeSqft: deal.sizeSqft ? parseFloat(deal.sizeSqft) : null,
    askingPrice: parseFloat(deal.askingPrice),
    estimatedMarketPrice: parseFloat(deal.estimatedMarketPrice),
    discountPercentage: deal.discountPercentage ? parseFloat(deal.discountPercentage) : null,
    urgencyScore: deal.urgencyScore,
    urgencyReason: deal.urgencyReason,
    sellerTimeline: deal.sellerTimeline,
    propertyStatus: deal.propertyStatus,
    paymentPlanDetails: deal.paymentPlanDetails,
    brokerContact: deal.brokerContact,
    imageUrl: deal.imageUrl,
    submittedByUserId: deal.submittedByUserId,
    status: deal.status,
    isVipOnly: deal.isVipOnly,
    aiDistressScore: deal.aiDistressScore ?? null,
    aiInvestorSummary: deal.aiInvestorSummary ?? null,
    aiRiskFlags: deal.aiRiskFlags ?? null,
    aiRecommendedBuyerType: deal.aiRecommendedBuyerType ?? null,
    aiLiquidityRating: deal.aiLiquidityRating ?? null,
    aiNegotiationAngle: deal.aiNegotiationAngle ?? null,
    createdAt: deal.createdAt,
    submittedBy: null,
  };
}

async function getRoomWithCounts(roomId: number) {
  const [itemCount] = await db
    .select({ count: count() })
    .from(dealRoomItemsTable)
    .where(eq(dealRoomItemsTable.dealRoomId, roomId));
  const [bidCount] = await db
    .select({ count: count() })
    .from(bidsTable)
    .where(eq(bidsTable.dealRoomId, roomId));
  return { itemCount: itemCount.count, bidCount: bidCount.count };
}

// ─── Investor: list accessible deal rooms ────────────────────────────────────
router.get("/deal-rooms", requireAuth, async (req, res): Promise<void> => {
  const { auth } = req;

  let rooms: (typeof dealRoomsTable.$inferSelect)[];
  if (auth!.role === "admin") {
    rooms = await db.select().from(dealRoomsTable).orderBy(sql`${dealRoomsTable.startTime} DESC`);
  } else {
    const membership = auth!.membership;
    const accessLevels: Array<"vip" | "elite" | "invite_only"> = [];
    if (membership === "vip") accessLevels.push("vip");
    if (membership === "elite") accessLevels.push("vip", "elite");

    if (accessLevels.length === 0) {
      res.json([]);
      return;
    }

    rooms = await db
      .select()
      .from(dealRoomsTable)
      .where(inArray(dealRoomsTable.accessLevel, accessLevels))
      .orderBy(sql`${dealRoomsTable.startTime} DESC`);
  }

  const result = await Promise.all(
    rooms.map(async (room) => {
      const { itemCount, bidCount } = await getRoomWithCounts(room.id);
      return {
        id: room.id,
        title: room.title,
        description: room.description,
        startTime: room.startTime,
        endTime: room.endTime,
        status: room.status,
        accessLevel: room.accessLevel,
        createdAt: room.createdAt,
        itemCount,
        bidCount,
      };
    })
  );

  res.json(result);
});

// ─── Investor: get deal room detail with items ────────────────────────────────
router.get("/deal-rooms/:id", requireAuth, async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);
  if (isNaN(id)) { res.status(400).json({ error: "Invalid id" }); return; }

  const [room] = await db.select().from(dealRoomsTable).where(eq(dealRoomsTable.id, id));
  if (!room) { res.status(404).json({ error: "Deal room not found" }); return; }

  const auth = req.auth!;
  if (auth.role !== "admin") {
    const membership = auth.membership;
    const allowed =
      (room.accessLevel === "vip" && (membership === "vip" || membership === "elite")) ||
      (room.accessLevel === "elite" && membership === "elite");
    if (!allowed) {
      res.status(403).json({ error: "Your membership level does not grant access to this deal room" });
      return;
    }
  }

  const rawItems = await db
    .select({ item: dealRoomItemsTable, deal: dealsTable })
    .from(dealRoomItemsTable)
    .leftJoin(dealsTable, eq(dealRoomItemsTable.dealId, dealsTable.id))
    .where(eq(dealRoomItemsTable.dealRoomId, id));

  const items = await Promise.all(
    rawItems.map(async (row) => {
      if (!row.deal) return null;
      const [bidCountRow] = await db
        .select({ count: count() })
        .from(bidsTable)
        .where(and(eq(bidsTable.dealRoomId, id), eq(bidsTable.dealId, row.item.dealId)));
      const bidCount = bidCountRow.count;
      return {
        id: row.item.id,
        dealRoomId: row.item.dealRoomId,
        dealId: row.item.dealId,
        reservePrice: row.item.reservePrice ? parseFloat(row.item.reservePrice) : null,
        minimumOffer: row.item.minimumOffer ? parseFloat(row.item.minimumOffer) : null,
        sellerTerms: row.item.sellerTerms,
        closingTimeline: row.item.closingTimeline,
        createdAt: row.item.createdAt,
        deal: formatDealForRoom(row.deal),
        bidCount,
        hasHighInterest: bidCount >= 3,
      };
    })
  );

  const { itemCount, bidCount } = await getRoomWithCounts(id);

  res.json({
    id: room.id,
    title: room.title,
    description: room.description,
    startTime: room.startTime,
    endTime: room.endTime,
    status: room.status,
    accessLevel: room.accessLevel,
    createdAt: room.createdAt,
    itemCount,
    bidCount,
    items: items.filter(Boolean),
  });
});

// ─── Investor: submit a confidential offer ────────────────────────────────────
router.post("/deal-rooms/:id/bids", requireAuth, requireRole("investor"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const roomId = parseInt(raw, 10);
  if (isNaN(roomId)) { res.status(400).json({ error: "Invalid room id" }); return; }

  const [room] = await db.select().from(dealRoomsTable).where(eq(dealRoomsTable.id, roomId));
  if (!room) { res.status(404).json({ error: "Deal room not found" }); return; }

  const auth = req.auth!;
  const membership = auth.membership;
  const allowed =
    (room.accessLevel === "vip" && (membership === "vip" || membership === "elite")) ||
    (room.accessLevel === "elite" && membership === "elite");
  if (!allowed) {
    res.status(403).json({ error: "Your membership level does not grant access to this deal room" });
    return;
  }

  if (room.status !== "live") {
    res.status(400).json({ error: "This deal room is not currently accepting offers" });
    return;
  }

  const { dealId, offerAmount, message, proofOfFundsUrl } = req.body as {
    dealId: number;
    offerAmount: number;
    message?: string;
    proofOfFundsUrl?: string;
  };

  if (!dealId || !offerAmount || offerAmount <= 0) {
    res.status(400).json({ error: "dealId and a positive offerAmount are required" });
    return;
  }

  const [item] = await db
    .select()
    .from(dealRoomItemsTable)
    .where(and(eq(dealRoomItemsTable.dealRoomId, roomId), eq(dealRoomItemsTable.dealId, dealId)));
  if (!item) { res.status(404).json({ error: "Deal not found in this room" }); return; }

  if (item.minimumOffer && offerAmount < parseFloat(item.minimumOffer)) {
    res.status(400).json({ error: `Offer must be at least AED ${parseFloat(item.minimumOffer).toLocaleString()}` });
    return;
  }

  const [existing] = await db
    .select()
    .from(bidsTable)
    .where(and(
      eq(bidsTable.dealRoomId, roomId),
      eq(bidsTable.dealId, dealId),
      eq(bidsTable.investorUserId, auth.userId)
    ));

  if (existing) {
    const [updated] = await db
      .update(bidsTable)
      .set({ offerAmount: String(offerAmount), message: message ?? null, proofOfFundsUrl: proofOfFundsUrl ?? null, status: "submitted" })
      .where(eq(bidsTable.id, existing.id))
      .returning();
    res.status(201).json({
      ...updated,
      offerAmount: parseFloat(updated.offerAmount),
    });
    return;
  }

  const [bid] = await db
    .insert(bidsTable)
    .values({
      dealRoomId: roomId,
      dealId,
      investorUserId: auth.userId,
      offerAmount: String(offerAmount),
      message: message ?? null,
      proofOfFundsUrl: proofOfFundsUrl ?? null,
      status: "submitted",
    })
    .returning();

  res.status(201).json({
    ...bid,
    offerAmount: parseFloat(bid.offerAmount),
  });
});

// ─── Investor: get own bid for a deal in a room ───────────────────────────────
router.get("/deal-rooms/:id/my-bid", requireAuth, requireRole("investor"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const roomId = parseInt(raw, 10);
  const dealId = parseInt(String(req.query.dealId), 10);
  if (isNaN(roomId) || isNaN(dealId)) { res.status(400).json({ error: "Invalid ids" }); return; }

  const [bid] = await db
    .select()
    .from(bidsTable)
    .where(and(
      eq(bidsTable.dealRoomId, roomId),
      eq(bidsTable.dealId, dealId),
      eq(bidsTable.investorUserId, req.auth!.userId)
    ));

  if (!bid) { res.status(404).json({ error: "No bid found" }); return; }
  res.json({ ...bid, offerAmount: parseFloat(bid.offerAmount) });
});

// ─── Admin: list all deal rooms ───────────────────────────────────────────────
router.get("/admin/deal-rooms", requireAuth, requireRole("admin"), async (_req, res): Promise<void> => {
  const rooms = await db.select().from(dealRoomsTable).orderBy(sql`${dealRoomsTable.startTime} DESC`);
  const result = await Promise.all(
    rooms.map(async (room) => {
      const { itemCount, bidCount } = await getRoomWithCounts(room.id);
      return {
        id: room.id,
        title: room.title,
        description: room.description,
        startTime: room.startTime,
        endTime: room.endTime,
        status: room.status,
        accessLevel: room.accessLevel,
        createdAt: room.createdAt,
        itemCount,
        bidCount,
      };
    })
  );
  res.json(result);
});

// ─── Admin: create deal room ──────────────────────────────────────────────────
router.post("/admin/deal-rooms", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const { title, description, startTime, endTime, accessLevel } = req.body as {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    accessLevel: "vip" | "elite" | "invite_only";
  };

  if (!title || !startTime || !endTime || !accessLevel) {
    res.status(400).json({ error: "title, startTime, endTime, and accessLevel are required" });
    return;
  }

  const [room] = await db
    .insert(dealRoomsTable)
    .values({
      title,
      description: description ?? null,
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      accessLevel,
      status: "upcoming",
    })
    .returning();

  res.status(201).json({ ...room, itemCount: 0, bidCount: 0 });
});

// ─── Admin: update deal room ──────────────────────────────────────────────────
router.put("/admin/deal-rooms/:id", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);
  if (isNaN(id)) { res.status(400).json({ error: "Invalid id" }); return; }

  const { title, description, startTime, endTime, status, accessLevel } = req.body as {
    title?: string;
    description?: string;
    startTime?: string;
    endTime?: string;
    status?: "upcoming" | "live" | "closed";
    accessLevel?: "vip" | "elite" | "invite_only";
  };

  const updateData: Partial<typeof dealRoomsTable.$inferInsert> = {};
  if (title !== undefined) updateData.title = title;
  if (description !== undefined) updateData.description = description;
  if (startTime !== undefined) updateData.startTime = new Date(startTime);
  if (endTime !== undefined) updateData.endTime = new Date(endTime);
  if (status !== undefined) updateData.status = status;
  if (accessLevel !== undefined) updateData.accessLevel = accessLevel;

  const [room] = await db.update(dealRoomsTable).set(updateData).where(eq(dealRoomsTable.id, id)).returning();
  if (!room) { res.status(404).json({ error: "Deal room not found" }); return; }

  const { itemCount, bidCount } = await getRoomWithCounts(id);
  res.json({ ...room, itemCount, bidCount });
});

// ─── Admin: add deal to room ──────────────────────────────────────────────────
router.post("/admin/deal-rooms/:id/items", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const roomId = parseInt(raw, 10);
  if (isNaN(roomId)) { res.status(400).json({ error: "Invalid room id" }); return; }

  const { dealId, reservePrice, minimumOffer, sellerTerms, closingTimeline } = req.body as {
    dealId: number;
    reservePrice?: number;
    minimumOffer?: number;
    sellerTerms?: string;
    closingTimeline?: string;
  };

  if (!dealId) { res.status(400).json({ error: "dealId is required" }); return; }

  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, dealId));
  if (!deal || deal.status !== "approved") {
    res.status(400).json({ error: "Only approved deals can be added to a deal room" });
    return;
  }

  const [item] = await db
    .insert(dealRoomItemsTable)
    .values({
      dealRoomId: roomId,
      dealId,
      reservePrice: reservePrice != null ? String(reservePrice) : null,
      minimumOffer: minimumOffer != null ? String(minimumOffer) : null,
      sellerTerms: sellerTerms ?? null,
      closingTimeline: closingTimeline ?? null,
    })
    .returning();

  res.status(201).json({
    id: item.id,
    dealRoomId: item.dealRoomId,
    dealId: item.dealId,
    reservePrice: item.reservePrice ? parseFloat(item.reservePrice) : null,
    minimumOffer: item.minimumOffer ? parseFloat(item.minimumOffer) : null,
    sellerTerms: item.sellerTerms,
    closingTimeline: item.closingTimeline,
    createdAt: item.createdAt,
    deal: formatDealForRoom(deal),
    bidCount: 0,
    hasHighInterest: false,
  });
});

// ─── Admin: remove deal from room ────────────────────────────────────────────
router.delete("/admin/deal-rooms/:id/items/:itemId", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const rawRoomId = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const rawItemId = Array.isArray(req.params.itemId) ? req.params.itemId[0] : req.params.itemId;
  const roomId = parseInt(rawRoomId, 10);
  const itemId = parseInt(rawItemId, 10);
  if (isNaN(roomId) || isNaN(itemId)) { res.status(400).json({ error: "Invalid ids" }); return; }

  const [item] = await db
    .delete(dealRoomItemsTable)
    .where(and(eq(dealRoomItemsTable.id, itemId), eq(dealRoomItemsTable.dealRoomId, roomId)))
    .returning();
  if (!item) { res.status(404).json({ error: "Item not found" }); return; }
  res.sendStatus(204);
});

// ─── Admin: list all bids for a room ─────────────────────────────────────────
router.get("/admin/deal-rooms/:id/bids", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const roomId = parseInt(raw, 10);
  if (isNaN(roomId)) { res.status(400).json({ error: "Invalid room id" }); return; }

  const rows = await db
    .select({ bid: bidsTable, investor: usersTable, deal: dealsTable })
    .from(bidsTable)
    .leftJoin(usersTable, eq(bidsTable.investorUserId, usersTable.id))
    .leftJoin(dealsTable, eq(bidsTable.dealId, dealsTable.id))
    .where(eq(bidsTable.dealRoomId, roomId))
    .orderBy(sql`${bidsTable.createdAt} DESC`);

  res.json(
    rows.map((row) => ({
      id: row.bid.id,
      dealRoomId: row.bid.dealRoomId,
      dealId: row.bid.dealId,
      investorUserId: row.bid.investorUserId,
      offerAmount: parseFloat(row.bid.offerAmount),
      message: row.bid.message,
      proofOfFundsUrl: row.bid.proofOfFundsUrl,
      status: row.bid.status,
      createdAt: row.bid.createdAt,
      investor: row.investor
        ? { id: row.investor.id, name: row.investor.name, email: row.investor.email, membership: row.investor.membership }
        : { id: row.bid.investorUserId, name: "Unknown", email: "", membership: "free" },
      dealTitle: row.deal?.title ?? "Unknown",
    }))
  );
});

// ─── Admin: update bid status ─────────────────────────────────────────────────
router.put("/admin/bids/:id", requireAuth, requireRole("admin"), async (req, res): Promise<void> => {
  const raw = Array.isArray(req.params.id) ? req.params.id[0] : req.params.id;
  const id = parseInt(raw, 10);
  if (isNaN(id)) { res.status(400).json({ error: "Invalid bid id" }); return; }

  const { status } = req.body as { status: "submitted" | "shortlisted" | "rejected" | "accepted" };
  if (!status) { res.status(400).json({ error: "status is required" }); return; }

  const [bid] = await db.update(bidsTable).set({ status }).where(eq(bidsTable.id, id)).returning();
  if (!bid) { res.status(404).json({ error: "Bid not found" }); return; }

  const [investor] = await db.select().from(usersTable).where(eq(usersTable.id, bid.investorUserId));
  const [deal] = await db.select().from(dealsTable).where(eq(dealsTable.id, bid.dealId));

  res.json({
    id: bid.id,
    dealRoomId: bid.dealRoomId,
    dealId: bid.dealId,
    investorUserId: bid.investorUserId,
    offerAmount: parseFloat(bid.offerAmount),
    message: bid.message,
    proofOfFundsUrl: bid.proofOfFundsUrl,
    status: bid.status,
    createdAt: bid.createdAt,
    investor: investor
      ? { id: investor.id, name: investor.name, email: investor.email, membership: investor.membership }
      : { id: bid.investorUserId, name: "Unknown", email: "", membership: "free" },
    dealTitle: deal?.title ?? "Unknown",
  });
});

export default router;
